import { z } from 'zod';

/**
 * Versioned operation-based synchronisation protocol (spec §15).
 *
 * This is deliberately NOT "copy the database periodically". Every local
 * transaction writes its domain change and an outbox entry atomically; the
 * outbox entry is the unit that travels. The server is the authority on
 * ordering and acceptance; client clocks are provenance only.
 */
export const SYNC_PROTOCOL_VERSION = 1;

/** Entity types that participate in synchronisation. */
export const SyncEntity = z.enum([
  'patient',
  'patient_contact',
  'medical_history',
  'allergy',
  'medical_alert',
  'tooth_observation',
  'surface_finding',
  'visit',
  'treatment',
  'clinical_note',
  'note_amendment',
  'attachment',
  'imaging_study',
  'acquisition',
  'prescription',
  'appointment',
  'invoice',
  'invoice_line',
  'payment',
  'payment_allocation',
  'ledger_entry',
  'lab_order',
  'lab_order_event',
  'stock_movement',
  'audit_event',
  'zone_observation',
  'attachment_link',
  'treatment_plan',
  'care_assignment',
  'clinic',
  'membership',
  'clinic_resource',
  'working_hours',
  'availability_exception',
  'treatment_catalogue',
  'credit_note',
  'installment',
  'material',
  'supplier',
  'laboratory',
  'patient_source',
]);
export type SyncEntity = z.infer<typeof SyncEntity>;

export const SyncAction = z.enum([
  'create',
  'update',
  /** Append-only event (clinical notes, lab events, audit). Never conflicts. */
  'append',
  /** Immutable financial/stock fact. Idempotent, never merged by overwrite. */
  'post',
  'archive',
  'restore',
  'delete',
]);
export type SyncAction = z.infer<typeof SyncAction>;

export const SyncOperation = z.object({
  /** Client-generated, globally unique. THE idempotency key. A retry reuses it. */
  operationId: z.uuid(),
  protocolVersion: z.number().int().positive(),
  /** Schema version of `payload`, so an older client's shape stays interpretable. */
  schemaVersion: z.number().int().positive(),
  deviceId: z.uuid(),
  userId: z.uuid(),
  clinicId: z.uuid(),
  entity: SyncEntity,
  entityId: z.uuid(),
  action: SyncAction,
  /**
   * Entity version the client based this change on. null for `create` and for
   * append-only actions. Used for optimistic-concurrency conflict detection.
   */
  baseVersion: z.number().int().nonnegative().nullable(),
  /**
   * When the change happened, as the device's clock said it, corrected by the
   * offset the device measured against the server at its last contact (so a
   * desk whose clock runs ten minutes fast does not win every tie). Decides
   * "newest wins"; never authority over whether the change is accepted.
   */
  occurredAt: z.iso.datetime(),
  /** The correction applied to `occurredAt`, in milliseconds, for the record. */
  clockOffsetMs: z.number().int().optional(),
  /** Operations that must be accepted before this one is applicable. */
  dependsOn: z.array(z.uuid()).default([]),
  payload: z.record(z.string(), z.unknown()),
});
export type SyncOperation = z.infer<typeof SyncOperation>;

export const PushRequest = z.object({
  deviceId: z.uuid(),
  clinicId: z.uuid(),
  /** Operations in dependency order. The server applies each atomically. */
  operations: z.array(SyncOperation).max(500),
});
export type PushRequest = z.infer<typeof PushRequest>;

export const OperationStatus = z.enum([
  /** Applied now. */
  'accepted',
  /** Already applied by an earlier attempt; the same server version is returned. */
  'duplicate',
  /** Business rule, permission or entitlement refused it. Terminal. */
  'rejected',
  /** Base version stale — needs domain conflict handling on the client. */
  'conflict',
  /** A dependency was not accepted; retry after the dependency resolves. */
  'deferred',
]);
export type OperationStatus = z.infer<typeof OperationStatus>;

export const RejectionCode = z.enum([
  'permission_denied',
  'entitlement_denied',
  'membership_revoked',
  'validation_failed',
  'tenant_mismatch',
  'unknown_entity',
  'schema_unsupported',
  'dependency_rejected',
  'immutable_record',
]);
export type RejectionCode = z.infer<typeof RejectionCode>;

export const OperationResult = z.object({
  operationId: z.uuid(),
  status: OperationStatus,
  entityId: z.uuid().nullable(),
  /** Authoritative version after applying. Stable across duplicate retries. */
  serverVersion: z.number().int().nonnegative().nullable(),
  code: RejectionCode.nullable(),
  message: z.string().nullable(),
  /** Server state for the client to reconcile against, on `conflict`. */
  serverState: z.record(z.string(), z.unknown()).nullable(),
  /**
   * What the rules did with an accepted operation beyond applying it as
   * sent: 'superseded' (a newer change already held the field), 'overlap'
   * (a booking accepted on top of another), 'duplicate' (the same thing
   * already existed; nothing added), 'line_dropped', 'invoice_dropped',
   * 'not_applied' (a business rule refused it; the server's record stands),
   * 'numbered', 'credit', 'stock_negative', 'possible_duplicate'.
   */
  flags: z.array(z.string()).default([]),
  /** A sentence about it, for the device's log. */
  note: z.string().nullable().default(null),
});
export type OperationResult = z.infer<typeof OperationResult>;

export const PushResponse = z.object({
  results: z.array(OperationResult),
  /** Cursor the client may pull from to observe its own accepted writes. */
  cursor: z.string(),
});
export type PushResponse = z.infer<typeof PushResponse>;

export const PullRequest = z.object({
  deviceId: z.uuid(),
  clinicId: z.uuid(),
  /** Opaque, stable, monotonic. Empty string means "from the beginning". */
  cursor: z.string(),
  limit: z.number().int().positive().max(1000).default(200),
});
export type PullRequest = z.infer<typeof PullRequest>;

export const ChangeFeedEntry = z.object({
  cursor: z.string(),
  entity: SyncEntity,
  entityId: z.uuid(),
  clinicId: z.uuid(),
  action: SyncAction,
  serverVersion: z.number().int().nonnegative(),
  serverTime: z.iso.datetime(),
  /** null for tombstones and for records the caller may no longer read. */
  payload: z.record(z.string(), z.unknown()).nullable(),
  deleted: z.boolean(),
});
export type ChangeFeedEntry = z.infer<typeof ChangeFeedEntry>;

/**
 * Instruction to drop locally cached data the device is no longer authorised to
 * hold (membership narrowed, patient reassigned, plan downgraded). Spec §15.
 */
export const PurgeInstruction = z.object({
  entity: SyncEntity,
  entityId: z.uuid().nullable(),
  /** null entityId + scope='clinic' means purge everything for that clinic. */
  scope: z.enum(['entity', 'patient', 'clinic']),
  patientId: z.uuid().nullable(),
  reason: z.string(),
});
export type PurgeInstruction = z.infer<typeof PurgeInstruction>;

export const PullResponse = z.object({
  changes: z.array(ChangeFeedEntry),
  purges: z.array(PurgeInstruction),
  nextCursor: z.string(),
  hasMore: z.boolean(),
  /**
   * True when the requested cursor is older than the server's tombstone
   * retention window: the client must resnapshot WITHOUT discarding its outbox.
   */
  resnapshotRequired: z.boolean(),
});
export type PullResponse = z.infer<typeof PullResponse>;

/** Local outbox states on the device. */
export const OutboxState = z.enum([
  'pending',
  'in_flight',
  'acknowledged',
  'conflict',
  /** Server refused it and the user can no longer push: kept in the encrypted
   *  recovery queue for an authorised clinic owner to review (spec §15). */
  'recovery',
]);
export type OutboxState = z.infer<typeof OutboxState>;

/** Attachment transfer is separate from record sync, with explicit states. */
export const AttachmentTransferState = z.enum([
  'local_only',
  'pending_upload',
  'uploading',
  'uploaded',
  'verified',
  'failed',
]);
export type AttachmentTransferState = z.infer<typeof AttachmentTransferState>;
